import {useContext, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {Button, DataTable, IconButton, Title} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {Fund, useFunds} from './../hooks/useDomainData';
import {FundEditor} from '../components/FormikStoreEditors/FundEditor';
import {useAppSettings} from '../components/AppSettings';
import {useAuthPersonInfo} from '../hooks/usePersonInfo';

function Funds() {
  const user = useContext(UserContext);
  const person = useAuthPersonInfo();
  const funds = useFunds();
  const queryClient = useQueryClient();
  const appSettings = useAppSettings();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Fund | undefined
  >();

  const collectionName = 'funds';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: Fund) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: Fund) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };
  if (!user) {
    return null;
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Funds</Title>
        {(person?.isAdmin || person?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Fund
          </Button>
        )}
      </View>
      <ScrollView
        persistentScrollbar={true}
        style={[styles.container, styles.content]}>
        <DataTable>
          {!funds && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {funds && (
            <DataTable.Header style={styles.shortHeader}>
              <DataTable.Title style={styles.widerCell}>Name</DataTable.Title>
              <DataTable.Title style={styles.borderCell}>Code</DataTable.Title>
              <DataTable.Title style={styles.borderCell}>
                Internal?
              </DataTable.Title>
              <DataTable.Title style={styles.borderCell}>
                Accepting Reservations?
              </DataTable.Title>
              {(person?.isAdmin || person?.isGuestRelations) && (
                <DataTable.Title style={styles.borderCell}>
                  Actions
                </DataTable.Title>
              )}
            </DataTable.Header>
          )}
          {funds && funds.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No funds found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {funds &&
            funds.length > 0 &&
            funds.map(fund => (
              <DataTable.Row key={fund.fundId}>
                <DataTable.Cell style={styles.widerCell}>
                  {fund.fund}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {fund.fundCode}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {fund.internal && <Icon name={'check'} />}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {fund.acceptingReservations && <Icon name={'check'} />}
                </DataTable.Cell>
                {(person?.isAdmin || person?.isGuestRelations) && (
                  <DataTable.Cell style={styles.borderCell}>
                    <>
                      {(person?.isAdmin || person?.isGuestRelations) && (
                        <IconButton
                          icon="pencil"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() => handleEditFirestoreObject(fund)}
                        />
                      )}
                      {person?.isAdmin && (
                        <IconButton
                          icon="delete"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() =>
                            handleDeleteFirestoreObject.mutate(fund)
                          }
                        />
                      )}
                    </>
                  </DataTable.Cell>
                )}
              </DataTable.Row>
            ))}
        </DataTable>
      </ScrollView>
      <FundEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        fund={currentFirestoreObject}
      />
    </>
  );
}

const styles = StyleSheet.create({
  widerCell: {
    minWidth: 150,
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    paddingHorizontal: 20,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default Funds;
